import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Mail, Trash2, BellRing, Send, Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, PageState, StatusPill, Empty } from "@/components/app/Common";
import { SimpleSelect } from "@/components/app/SimpleSelect";
import { useLoad } from "@/hooks/useLoad";
import { api, blobUrl, errMsg } from "@/lib/api";
import { fmtDate, bandColor } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { EntityAccessPicker, AccessDialog, accessLabel } from "@/components/app/EntityAccess";

const ROLES = [{ value: "viewer", label: "View-only" }, { value: "editor", label: "Editor" }];

const Team = () => {
  const state = useLoad(() => Promise.all([api.get("/team/members"), api.get("/entities")]).then(([a, b]) => ({ ...a.data, ents: b.data })));
  const [f, setF] = useState({ email: "", role: "viewer", entity_ids: null });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = (await api.post("/team/invite", f)).data;
      toast.success(r.email_sent ? `Invitation emailed to ${r.email}` : `${r.email} added — invitation email could not be sent`);
      setF({ email: "", role: "viewer", entity_ids: null });
      state.reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <PageState state={state}>
      {state.data && (
        <div className="space-y-6">
          <form onSubmit={invite} className="surface p-6 flex flex-col md:flex-row md:flex-wrap gap-3 md:items-end" data-testid="invite-form">
            <div className="flex-1">
              <div className="font-semibold">Invite an accountant or staff member</div>
              <p className="text-sm text-muted-foreground mb-3">They sign in with this email (Google or password). Choose whether they see every entity or only the ones they work on.</p>
              <Input type="email" required placeholder="name@firm.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" />
            </div>
            <div className="md:w-44"><SimpleSelect value={f.role} onChange={(v) => setF({ ...f, role: v })} options={ROLES} testId="invite-role-select" /></div>
            <Button type="submit" disabled={busy || (Array.isArray(f.entity_ids) && f.entity_ids.length === 0)} data-testid="invite-submit-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}Send invite</Button>
            <div className="md:basis-full md:order-last">
              <EntityAccessPicker entities={state.data.ents} value={f.entity_ids} onChange={(v) => setF({ ...f, entity_ids: v })} testId="invite-access" />
            </div>
          </form>
          <div className="text-xs text-muted-foreground">{state.data.members.length} of {state.data.seats} seats used · View-only members can read everything and use the AI assistant; editors can also add and change records. Billing stays with you.</div>
          {state.data.members.length === 0 ? <Empty icon={Users} title="No team members yet" text="Invite your accountant, bookkeeper or operations lead." testId="team-empty" /> : (
            <div className="surface divide-y divide-border" data-testid="team-list">
              {state.data.members.map((m) => (
                <div key={m.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`member-row-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{m.name || m.email}</div>
                    <div className="text-xs text-muted-foreground">{m.email} · invited {fmtDate(m.invited_at)}</div>
                    <div className="text-xs mt-0.5" data-testid={`member-access-${m.id}`}>Access: <span className="font-medium">{accessLabel(m.entity_ids, state.data.ents)}</span></div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setEditing(m)} data-testid={`member-edit-access-${m.id}`}>Edit access</Button>
                  <StatusPill status={m.status === "active" ? "Active" : "Invited"} testId={`member-status-${m.id}`} />
                  <div className="w-36"><SimpleSelect value={m.role} onChange={(v) => run(() => api.put(`/team/members/${m.id}`, { role: v, entity_ids: m.entity_ids ?? null }), "Role updated")} options={ROLES} testId={`member-role-${m.id}`} /></div>
                  {m.status === "invited" && <Button size="sm" variant="outline" onClick={() => run(() => api.post(`/team/members/${m.id}/resend`), "Invitation re-sent")} data-testid={`member-resend-${m.id}`}><Mail className="h-3.5 w-3.5 mr-1" />Resend</Button>}
                  <Button size="icon" variant="ghost" onClick={() => window.confirm(`Remove ${m.email}?`) && run(() => api.delete(`/team/members/${m.id}`), "Member removed")} data-testid={`member-remove-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
          <AccessDialog member={editing} entities={state.data.ents} onClose={() => setEditing(null)}
            onSave={(ids) => run(() => api.put(`/team/members/${editing.id}`, { role: editing.role, entity_ids: ids }), "Access updated").then(() => setEditing(null))} />
        </div>
      )}
    </PageState>
  );
};

const Reminders = () => {
  const state = useLoad(() => api.get("/settings/reminders").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    try {
      await api.put("/settings/reminders", { enabled: v });
      toast.success(v ? "Reminders on" : "Reminders off");
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const preview = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/settings/reminders/preview")).data;
      toast.success(`Preview sent to ${r.sent_to}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const d = state.data;
  return (
    <PageState state={state}>
      {d && (
        <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
          <div className="surface p-6 space-y-5">
            <div className="flex items-start gap-4">
              <BellRing className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">Email deadline reminders</div>
                <p className="text-sm text-muted-foreground mt-1">Checked daily. You get one email when a deadline reaches {d.days.join(", ")} days out — each notice is sent once. Covers every filing and tax deadline on active entities.</p>
              </div>
              <Switch checked={d.enabled} onCheckedChange={toggle} data-testid="reminders-toggle" />
            </div>
            <Button variant="outline" onClick={preview} disabled={busy} data-testid="reminder-preview-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}Email me a preview now</Button>
            <div>
              <div className="text-sm font-medium mb-2">Recently sent</div>
              {d.recent.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="reminders-recent-empty">No reminders sent yet.</p> : (
                <ul className="space-y-1.5 text-sm" data-testid="reminders-recent">
                  {d.recent.map((r, i) => <li key={i} className="flex justify-between gap-3"><span className="truncate">{r.title} · <span className="text-muted-foreground">{r.entity}</span></span><span className="font-mono text-xs text-muted-foreground whitespace-nowrap">{r.days}d notice · {fmtDate(r.sent_at)}</span></li>)}
                </ul>
              )}
            </div>
          </div>
          <div className="surface p-6" data-testid="reminders-upcoming">
            <div className="font-semibold mb-3">Due in the next 30 days</div>
            {d.upcoming.length === 0 ? <p className="text-sm text-muted-foreground">Nothing due soon.</p> : (
              <ul className="space-y-2 text-sm">
                {d.upcoming.map((i) => (
                  <li key={i.id} className="flex items-center gap-3">
                    <span className={`font-mono text-xs w-12 ${i.days <= 3 ? "text-red-500" : i.days <= 14 ? "text-amber-500" : "text-blue-500"}`}>{i.days}d</span>
                    <span className="flex-1 min-w-0 truncate">{i.title} · <span className="text-muted-foreground">{i.entity}</span></span>
                    {i.already_sent ? <span className="text-[11px] text-muted-foreground">{i.bucket}d notice sent</span> : <span className="text-[11px] text-emerald-500">{i.bucket}d notice next run</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </PageState>
  );
};

const Digest = () => {
  const state = useLoad(() => api.get("/settings/digest").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    try {
      await api.put("/settings/digest", { enabled: v });
      toast.success(v ? "Weekly digest on" : "Weekly digest off");
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const preview = async () => {
    setBusy(true);
    try {
      toast.success(`Preview sent to ${(await api.post("/settings/digest/preview")).data.sent_to}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const d = state.data;
  const p = d?.preview;
  return (
    <PageState state={state}>
      {d && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="surface p-6 space-y-5">
            <div className="flex items-start gap-4">
              <Mail className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">Weekly health digest</div>
                <p className="text-sm text-muted-foreground mt-1">Every Monday: your Corporate Health Score, what changed since last week and your top three actions.</p>
                <p className="text-xs text-muted-foreground mt-2" data-testid="digest-last-sent">{d.last_sent ? `Last sent ${fmtDate(d.last_sent)}` : "Not sent yet: the first digest sets your starting point."}</p>
              </div>
              <Switch checked={d.enabled} onCheckedChange={toggle} data-testid="digest-toggle" />
            </div>
            <Button variant="outline" onClick={preview} disabled={busy} data-testid="digest-preview-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}Email me a preview now</Button>
          </div>
          <div className="surface p-6" data-testid="digest-preview">
            <div className="eyebrow mb-3">This week's digest</div>
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-4xl font-semibold" data-testid="digest-score">{p.score}</span>
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border" data-testid="digest-band"
                style={{ color: bandColor(p.band), borderColor: `${bandColor(p.band)}55`, background: `${bandColor(p.band)}1a` }}>{p.band}</span>
              <span className="text-sm text-muted-foreground">{p.delta != null ? `${p.delta >= 0 ? "+" : ""}${p.delta} vs last week` : "first digest"}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2">{p.filed} item(s) filed and {p.posted} receipt(s) posted in the last 7 days</div>
            <div className="text-sm font-medium mt-5 mb-2">Top 3 actions</div>
            <ol className="list-decimal pl-5 space-y-2 text-sm" data-testid="digest-actions">
              {p.actions.length === 0 ? <li>Nothing urgent.</li> : p.actions.map((a, i) => <li key={i}>{a.text}<div className="text-xs text-muted-foreground">{a.hint}</div></li>)}
            </ol>
          </div>
        </div>
      )}
    </PageState>
  );
};

const BIZ_FIELDS = [
  ["legal_name", "Company legal name", "e.g. AccruallQ Inc."],
  ["tax_label", "Tax number label", "e.g. GST/HST No., EIN, VAT No."],
  ["tax_number", "Tax number", "e.g. 123456789 RT0001"],
  ["email", "Billing contact email", "e.g. billing@yourcompany.com"],
];

const BusinessDetails = () => {
  const state = useLoad(() => api.get("/admin/business").then((r) => r.data));
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const f = form || state.data || {};
  const set = (k) => (e) => setForm({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/business", { legal_name: "", address: "", tax_label: "", tax_number: "", email: "", ...f });
      setForm(r.data);
      toast.success("Business details saved — new receipts will show them");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const preview = async () => {
    try {
      window.open(await blobUrl("/admin/business/sample-receipt"), "_blank");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <PageState state={state}>
      <div className="surface p-6 max-w-2xl" data-testid="business-panel">
        <div className="font-semibold">Business details on receipts</div>
        <div className="text-sm text-muted-foreground mb-5">Shown as “Issued by” on every PDF and emailed receipt so customers can use them for bookkeeping.</div>
        <div className="grid gap-4 sm:grid-cols-2">
          {BIZ_FIELDS.slice(0, 1).map(([k, l, ph]) => (
            <label key={k} className="sm:col-span-2 text-sm">{l}<Input className="mt-1.5" value={f[k] || ""} onChange={set(k)} placeholder={ph} data-testid={`business-${k}`} /></label>
          ))}
          <label className="sm:col-span-2 text-sm">Business address
            <textarea rows={3} value={f.address || ""} onChange={set("address")} placeholder={"Street\nCity, Province/State Postal code\nCountry"}
              className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring" data-testid="business-address" />
          </label>
          {BIZ_FIELDS.slice(1).map(([k, l, ph]) => (
            <label key={k} className="text-sm">{l}<Input className="mt-1.5" value={f[k] || ""} onChange={set(k)} placeholder={ph} data-testid={`business-${k}`} /></label>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={save} disabled={busy} data-testid="business-save-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save details</Button>
          <Button variant="outline" onClick={preview} data-testid="business-preview-btn">Preview sample receipt</Button>
        </div>
      </div>
    </PageState>
  );
};

const LEVEL = { high: "Applies", watch: "Watch", clear: "Fine" };

const exportCsv = (rows) => {
  const head = ["email", "opted_in", "missed_filings", "tax_problems", "lost_records", "revival_costs", "tips_sent", "created_at"];
  const body = rows.map((l) => [l.email, l.marketing_opt_in && !l.unsubscribed ? "yes" : "no", ...["Missed Filings", "Tax Problems", "Lost Records", "Revival Costs"].map((k) => l.results?.[k] || ""), l.followup ? `${l.followup.sent}/${l.followup.total}` : "", l.created_at]);
  const csv = [head, ...body].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "accruallq-mailing-list.csv";
  a.click();
};

const Leads = () => {
  const state = useLoad(() => api.get("/admin/leads").then((r) => r.data));
  const rows = state.data || [];
  return (
    <PageState state={state}>
      <div className="surface p-6" data-testid="leads-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-semibold">Risk check mailing list</div>
            <div className="text-sm text-muted-foreground">{rows.length} {rows.length === 1 ? "visitor" : "visitors"} emailed their results · {rows.filter((l) => l.marketing_opt_in && !l.unsubscribed).length} opted in to updates</div>
          </div>
          <Button variant="outline" size="sm" disabled={!rows.length} onClick={() => exportCsv(rows)} data-testid="leads-export-btn">Export CSV</Button>
        </div>
        {!rows.length ? <Empty icon={Mail} title="No sign-ups yet" text="Visitors who email themselves their Free Risk Check results will appear here." testId="leads-empty" /> : (
          <div className="divide-y divide-border">
            {rows.map((l) => (
              <div key={l.email} className="flex flex-wrap items-center gap-3 py-3 text-sm" data-testid="lead-row">
                <span className="font-medium flex-1 min-w-[200px] truncate">{l.email}</span>
                <span className="text-xs text-muted-foreground">{Object.values(l.results || {}).filter((v) => v === "high").length} of 4 risks</span>
                {l.alerted_at && <span className="text-xs rounded-full px-2 py-0.5 bg-red-500/10 text-red-600 dark:text-red-400" data-testid="lead-hot">High risk</span>}
                {l.followup && <span className="text-xs text-muted-foreground" data-testid="lead-followup">{l.unsubscribed && l.followup.sent < l.followup.total ? "Series stopped" : `Tips ${l.followup.sent}/${l.followup.total} sent`}</span>}
                <span className={`text-xs rounded-full px-2 py-0.5 ${l.marketing_opt_in && !l.unsubscribed ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>{l.unsubscribed ? "Unsubscribed" : l.marketing_opt_in ? "Opted in" : "Results only"}</span>
                <span className="text-xs text-muted-foreground w-24 text-right">{fmtDate(l.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageState>
  );
};

const LABELS = { briefing_refresh: "Briefing refreshed", rebalance_accept: "Rebalance accepted", task_update_email: "Task updated from email", waiting_review: "Waiting On review",
  decision_create: "Decision recorded", decision_review: "Decision outcome check", decision_checkin: "Results logged (app)", decision_checkin_email: "Results logged (email)",
  decision_similar: "Similar decision lookup", decision_insights: "Decision patterns", playbook_generate: "Playbook built", playbook_check: "Playbook check", portfolio_insights: "Portfolio insights" };
const FB = { playbook: "Playbook plays", playbook_check: "Playbook checks", decision_insights: "Decision patterns", decision_similar: "Similar decision lookups", briefing: "Weekly briefing", waiting_review: "Waiting On reviews", portfolio_insights: "Portfolio insights" };

const Adoption = () => {
  const state = useLoad(() => api.get("/admin/usage").then((r) => r.data));
  const d = state.data;
  const [sending, setSending] = useState(false);
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/usage/digest");
      toast.success(`Adoption digest sent to ${r.data.to}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSending(false);
  };
  return (
    <PageState state={state}>
      {d && <div className="surface p-6" data-testid="adoption-panel">
        <div className="float-right text-right"><Button size="sm" variant="outline" onClick={sendNow} disabled={sending} data-testid="adoption-send-btn">{sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Email me the digest</Button>
          <div className="text-[11px] text-muted-foreground mt-1">Sent automatically on the 1st of each month</div></div>
        <div className="font-semibold">Chief of Staff adoption · last {d.days} days</div>
        <div className="text-sm text-muted-foreground mb-4" data-testid="adoption-active">{d.active_workspaces} active workspace{d.active_workspaces === 1 ? "" : "s"} used AI Chief of Staff features</div>
        <div className="divide-y divide-border">{d.features.map((f) => (
          <div key={f.feature} className="flex items-center gap-3 py-2 text-sm" data-testid={`adoption-row-${f.feature}`}>
            <span className="flex-1">{LABELS[f.feature] || f.feature}</span><span className="font-mono w-16 text-right">{f.uses}</span><span className="text-xs text-muted-foreground w-28 text-right">{f.workspaces} workspace{f.workspaces === 1 ? "" : "s"}</span></div>))}</div>
        {Object.keys(d.feedback).length > 0 && <div className="mt-5"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Was it helpful?</div>
          {Object.entries(d.feedback).map(([k, v]) => <div key={k} className="text-sm flex justify-between py-1" data-testid={`adoption-feedback-${k}`}><span>{FB[k] || k}</span><span className="font-mono">{Math.round((v.helpful / v.total) * 100)}% helpful · {v.total} votes</span></div>)}</div>}
      </div>}
    </PageState>
  );
};

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  if (user?.workspace?.role !== "owner") {
    return (
      <div>
        <PageHeader eyebrow="Settings" title="Team & reminders" />
        <Empty icon={Users} title="Managed by the workspace owner" text={`You are a ${user?.workspace?.role === "viewer" ? "view-only member" : "editor"} of ${user?.workspace?.owner_name}'s workspace. Switch to your own workspace to manage your team and reminders.`} testId="settings-member-note" />
      </div>
    );
  }
  return (
    <div>
      <PageHeader eyebrow="Settings" title="Team & reminders" subtitle="Share your workspace and make sure nothing slips past a deadline." />
      <Tabs defaultValue="team">
        <TabsList className="mb-6 max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="team" data-testid="tab-team">Team access</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">Deadline reminders</TabsTrigger>
          <TabsTrigger value="digest" data-testid="tab-digest">Weekly digest</TabsTrigger>
          {isAdmin && <TabsTrigger value="leads" data-testid="tab-leads">Mailing list</TabsTrigger>}
          {isAdmin && <TabsTrigger value="business" data-testid="tab-business">Business details</TabsTrigger>}
          {isAdmin && <TabsTrigger value="adoption" data-testid="tab-adoption">Adoption</TabsTrigger>}
        </TabsList>
        <TabsContent value="team"><Team /></TabsContent>
        <TabsContent value="reminders"><Reminders /></TabsContent>
        <TabsContent value="digest"><Digest /></TabsContent>
        {isAdmin && <TabsContent value="leads"><Leads /></TabsContent>}
        {isAdmin && <TabsContent value="business"><BusinessDetails /></TabsContent>}
        {isAdmin && <TabsContent value="adoption"><Adoption /></TabsContent>}
      </Tabs>
    </div>
  );
}
